import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { HandHeart, ExternalLink, Home, Sprout, BookOpen, Microscope, Brain, Shield, Heart, KeyRound, Globe, GraduationCap, Receipt as ReceiptIcon } from "lucide-react";
import { DONATION_CATEGORIES, DONATION_CATEGORY_LABELS, STARGATE_PO_BOX_MAP, STARGATE_BASE_URL, type DonationCategory } from "@shared/models/chat";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";

interface TitheModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CATEGORY_ICONS: Record<DonationCategory, typeof Home> = {
  homelessness: Home,
  food_pantry: Sprout,
  canning_and_jarring_lessons: BookOpen,
  research: Microscope,
  education: GraduationCap,
  mental_health: Brain,
  veterans: Shield,
  widows: Heart,
  prison: KeyRound,
  missions: Globe,
};

async function openStargateLane(category: DonationCategory) {
  try {
    const res = await apiRequest("POST", "/api/bridge/outbound", { category, returnUrl: "/" });
    const data = await res.json();
    if (data.stargateUrl) {
      window.open(data.stargateUrl, "_blank", "noopener,noreferrer");
      return;
    }
  } catch (_e) {
    // noop
  }
  const poBox = STARGATE_PO_BOX_MAP[category];
  const url = `${STARGATE_BASE_URL}/park/third-temple/arrive?poBox=${poBox}&from=temple&category=${encodeURIComponent(category)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function openStargateOverview() {
  const url = `${STARGATE_BASE_URL}/park/third-temple/arrive?from=temple`;
  window.open(url, "_blank", "noopener,noreferrer");
}

export function TitheModal({ open, onOpenChange }: TitheModalProps) {
  const handleCategorySelect = (category: DonationCategory) => {
    openStargateLane(category);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandHeart className="h-5 w-5 text-amber-500" />
            Tithes & Offerings
          </DialogTitle>
          <DialogDescription>
            Choose where your gift goes. You'll be taken to our giving portal to complete your donation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {DONATION_CATEGORIES.map((cat) => {
            const Icon = CATEGORY_ICONS[cat];
            return (
              <Button
                key={cat}
                variant="outline"
                className={cn(
                  "w-full justify-start h-auto py-3 px-4 text-left gap-3",
                  "hover:ring-2 hover:ring-amber-500/50 hover:border-amber-500/50 transition-all"
                )}
                onClick={() => handleCategorySelect(cat)}
                data-testid={`button-tithe-category-${cat}`}
              >
                <Icon className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="text-sm">{DONATION_CATEGORY_LABELS[cat]}</span>
                <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground shrink-0" />
              </Button>
            );
          })}
        </div>

        <div className="pt-2 border-t space-y-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground gap-2"
            onClick={() => {
              openStargateOverview();
              onOpenChange(false);
            }}
            data-testid="button-tithe-view-all"
          >
            <ExternalLink className="h-3 w-3" />
            View all giving lanes
          </Button>
          <Link href="/giving/receipts" onClick={() => onOpenChange(false)}>
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground gap-2"
              data-testid="button-tithe-view-receipts"
            >
              <ReceiptIcon className="h-3 w-3" />
              View giving history
            </Button>
          </Link>
          <p className="text-xs text-center text-muted-foreground">
            100% of your gift goes to the selected cause. No login required.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
